import type { FC } from 'react';
import { memo, useCallback, useRef, useEffect, useMemo, useState } from 'react';
import Image from '../../../../shared/components/Image';
import StringMapper from '../../../../shared/components/StringMapper';
import LinkedJackpots from '../LinkedJackpots';
import { useJackpotStatisticsTrigger } from '../../hooks/useJackpotStatisticsTrigger';
import { useAppStore } from 'store/app/appStore';
import { useGetLinkedJackpots } from '../LinkedJackpots/hooks';
import { useJackpotsStore } from '../../store/jackpotsStoreSimple';
import { jackpotPanelAnimationManager } from '../../../../core/services/managers/JackpotPanelAnimationManager';
import type { StandardJackpotProps } from '../../types/jackpot.types';
import styles from './StandardJackpot.module.css';
import { SECTION_NAMES } from '@repo/common';
import { useSeasonalBranding } from '../../../../store/featureFlags/featureFlagsStore';
import { JACKPOT_LEVEL_ID_BY_KEY, JACKPOT_LEVEL_IDS, JACKPOT_LEVEL_LABELS } from '../../constants';

const grandBoostLevelId = JACKPOT_LEVEL_IDS.GRAND;
const majorBoostLevelId = JACKPOT_LEVEL_IDS.MAJOR;

const StandardJackpot: FC<StandardJackpotProps> = ({
    icons,
    values,
    currency,
    denomination,
    jackpotName,
    sectionName,
    className,
    statisticsLevelKeys
}) => {

    const linkedJackpots = useGetLinkedJackpots(sectionName);
    const numberRefs = useRef<{ [key: string]: HTMLSpanElement | null }>({});
    const currencyRefs = useRef<{ [key: string]: HTMLSpanElement | null }>({});
    const containerRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
    const levelRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
    const [isFullyLoaded, setIsFullyLoaded] = useState(false);

    // Track loading state for each level image.
    const [imageLoadStates, setImageLoadStates] = useState<{ [key: string]: boolean }>({});
    const [grandBoostLevelValue, setGrandBoostLevelValue] = useState('');
    const [majorBoostLevelValue, setMajorBoostLevelValue] = useState('');

    const branding = useSeasonalBranding();

    const getJackpotBoostValues = useCallback(() => {
        const store = useJackpotsStore.getState();
        const formattedGrandBoostValue = store.getJackpotBonusValue(jackpotName, grandBoostLevelId);
        const formattedMajorBoostValue = store.getJackpotBonusValue(jackpotName, majorBoostLevelId);
        if (sectionName === SECTION_NAMES.BELL_LINK_BOOST || sectionName === SECTION_NAMES.VIP_BELL_LINK_BOOST) {
            setGrandBoostLevelValue(formattedGrandBoostValue);
            setMajorBoostLevelValue(formattedMajorBoostValue);
        }
    }, [sectionName, jackpotName]);

    const getLinkedJackpotsOrder = useCallback(() => {
        if (sectionName === SECTION_NAMES.BELL_LINK ||
            sectionName === SECTION_NAMES.BELL_LINK_BOOST ||
            sectionName === SECTION_NAMES.GODS_AND_KINGS) {
            return [SECTION_NAMES.BELL_LINK, SECTION_NAMES.GODS_AND_KINGS, SECTION_NAMES.BELL_LINK_BOOST]
        }
        else if (sectionName === SECTION_NAMES.VIP_BELL_LINK ||
            sectionName === SECTION_NAMES.VIP_GODS_AND_KINGS ||
            sectionName === SECTION_NAMES.VIP_BELL_LINK_BOOST) {
            return [SECTION_NAMES.VIP_BELL_LINK, SECTION_NAMES.VIP_GODS_AND_KINGS, SECTION_NAMES.VIP_BELL_LINK_BOOST]
        }
        else return undefined;

    }, [sectionName]);

    // PERFORMANCE: Optimized display function to minimize style recalculations
    const updateDisplayFromStore = useCallback(() => {
        const levels = ['grand', 'major', 'minor', 'mini'];
        const levelIds = [1, 2, 3, 4];

        // Batch all DOM reads first to avoid layout thrashing
        const elementsToUpdate: Array<{
            element: HTMLSpanElement;
            oldValue: string;
            newValue: string;
            levelKey: string;
            needsResize: boolean;
        }> = [];

        levels.forEach((levelKey, index) => {
            const numberElement = numberRefs.current[levelKey];
            if (numberElement) {
                const formattedValue = useJackpotsStore.getState().getFormattedValue(jackpotName, levelIds[index]);
                const oldValue = numberElement.innerText;

                if (oldValue !== formattedValue) {
                    elementsToUpdate.push({
                        element: numberElement,
                        oldValue,
                        newValue: formattedValue,
                        levelKey,
                        needsResize: oldValue.length !== formattedValue.length
                    });
                }
            }
        });

        // Batch all DOM writes together to minimize style recalculations
        if (elementsToUpdate.length > 0) {
            requestAnimationFrame(() => {
                elementsToUpdate.forEach(({ element, newValue, levelKey, needsResize }) => {
                    element.innerText = newValue;

                    // Only set visibility once during initial load
                    if (element.style.visibility !== "visible") {
                        element.style.visibility = "visible";
                    }

                    // Trigger font resize if needed
                    if (needsResize) {
                        const panelId = `${jackpotName}_${levelKey}_${sectionName}`;
                        jackpotPanelAnimationManager.triggerFontResize(panelId);
                    }
                });
            });
        }
    }, [jackpotName, sectionName]);

    // PERFORMANCE: Direct store subscription with DOM updates (no React re-renders during animation)
    useEffect(() => {
        // Subscribe to store changes for all updates
        const unsubscribe = useJackpotsStore.subscribe(() => {
            if (isFullyLoaded) {
                updateDisplayFromStore();
            }
        });

        return unsubscribe;
    }, [isFullyLoaded, updateDisplayFromStore]);

    // Opens the statistics drawer through an imperative store action — no state, no
    // subscription, so it cannot re-render this component and disturb the odometer above.
    const statisticsHandlers = useJackpotStatisticsTrigger({ jackpotName, sectionName });

    const openType = useAppStore((state) => state.settings.openType);

    const levels = useMemo(() => [
        { key: 'grand', label: JACKPOT_LEVEL_LABELS[JACKPOT_LEVEL_IDS.GRAND], value: values.grand, icon: icons.grand, boostValue: grandBoostLevelValue },
        { key: 'major', label: JACKPOT_LEVEL_LABELS[JACKPOT_LEVEL_IDS.MAJOR], value: values.major, icon: icons.major, boostValue: majorBoostLevelValue },
        { key: 'minor', label: JACKPOT_LEVEL_LABELS[JACKPOT_LEVEL_IDS.MINOR], value: values.minor, icon: icons.minor, boostValue: '' },
        { key: 'mini', label: JACKPOT_LEVEL_LABELS[JACKPOT_LEVEL_IDS.MINI], value: values.mini, icon: icons.mini, boostValue: '' },
    ], [values.grand, values.major, values.minor, values.mini, icons.grand, icons.major, icons.minor, icons.mini, grandBoostLevelValue, majorBoostLevelValue]);

    // Level mapping removed - not needed with direct store subscription

    // Handle image load completion for any image (panel icon or level images)
    const handleImageLoad = useCallback((imageKey: string) => {
        setImageLoadStates((prev) => ({
            ...prev,
            [imageKey]: true,
        }));
    }, []);

    // Check if all images are loaded
    const allImagesLoaded = useMemo(() => {
        return levels.every((level) => !level.icon || imageLoadStates[level.key] === true);
    }, [levels, imageLoadStates]);

    // Panel registration removed - handled by direct store subscription

    // Register panels when DOM elements are ready
    useEffect(() => {
        let attempts = 0;

        // Use requestAnimationFrame to ensure DOM is ready after tab switch
        const registerPanelsWhenReady = () => {

            requestAnimationFrame(() => {

                const activeLevels = ['grand', 'major', 'minor', 'mini']
                    .map((key) => levels.find((l) => l.key === key))
                    .filter((level) => level && level.icon);

                const allRefsReady = activeLevels.every((level) => {
                    return (
                        level &&
                        numberRefs.current[level.key] &&
                        currencyRefs.current[level.key] &&
                        containerRefs.current[level.key] &&
                        levelRefs.current[level.key]
                    );
                });

                // Show values when all refs and images are ready
                if (allRefsReady) {
                    // Register each level with the panel animation manager for font scaling
                    const levelMapping = JACKPOT_LEVEL_ID_BY_KEY;
                    activeLevels.forEach((level) => {
                        if (!level) return;

                        const levelEl = levelRefs.current[level.key];
                        const numberEl = numberRefs.current[level.key];
                        const currencyEl = currencyRefs.current[level.key];
                        const containerEl = containerRefs.current[level.key];
                        if (!levelEl || !numberEl || !currencyEl || !containerEl) return;

                        const panelId = `${jackpotName}_${level.key}_${sectionName}`;
                        jackpotPanelAnimationManager.registerPanel(
                            panelId,
                            jackpotName,
                            level.key,
                            levelMapping[level.key],
                            currency,
                            denomination,
                            levelEl,
                            numberEl,
                            currencyEl,
                            containerEl
                        );
                    });

                    getJackpotBoostValues();
                    updateDisplayFromStore(); // Show initial values from store
                    if (allImagesLoaded) setTimeout(() => setIsFullyLoaded(true), 35);
                } else if (!allRefsReady && attempts <= 10) {
                    attempts++;
                    setTimeout(registerPanelsWhenReady, 10);
                }
            });
        };

        registerPanelsWhenReady();

        // Cleanup: unregister panels on unmount
        return () => {
            const levelMapping = JACKPOT_LEVEL_ID_BY_KEY;
            Object.keys(levelMapping).forEach((levelKey) => {
                const panelId = `${jackpotName}_${levelKey}_${sectionName}`;
                jackpotPanelAnimationManager.unregisterPanel(panelId);
            });
        };
    }, [
        allImagesLoaded,
        sectionName,
        jackpotName,
        levels,
        updateDisplayFromStore,
        getJackpotBoostValues,
        currency,
        denomination,
    ]);

    // PERFORMANCE: Display updates now handled by direct store subscription above

    return (<>
        <div 
            className={`${styles.standardJackpot} ${!linkedJackpots?.length && styles.noLinkedJackpots} ${className || ''}`} 
            data-open-type={openType}
            data-is-fully-loaded={isFullyLoaded}
        >
            {/* Panel Icon/Logo */}
            {icons.panel && (
                <div className={styles.panelIcon} data-type='panelIcon' data-branding={branding}>
                    <Image
                        src={icons.panel}
                        alt={jackpotName}
                        loading="eager"
                        decoding="sync"
                    />
                </div>
            )}

            {/* Jackpot Levels */}
            <div className={styles.levelsContainer} data-type="levelsContainer" data-open-type={openType}>
                {levels.map((level) => {
                    // Rows without statistics behind them get NO pointer handlers at all, rather
                    // than a handler that returns early.
                    const isClickable = Boolean(statisticsLevelKeys?.includes(level.key));
                    return level.icon && (
                    <div
                        key={level.key}
                        ref={(el) => {
                          levelRefs.current[level.key] = el;
                          return undefined;
                        }}
                        data-level={level.key}
                        data-clickable={isClickable}
                        className={`${styles.level} ${styles[`level_${level.key}` as keyof typeof styles]}`}
                        {...(isClickable ? statisticsHandlers() : {})}
                    >
                        {/* Separate boost label image for better visual display */}
                        {level.boostValue && level.icon && 
                            <Image
                                src={`${level.icon.substring(0, level.icon.lastIndexOf('/') + 1)}boost`} // The boost label icon MUST be named boost.webp
                                alt={'boost'}
                                id={`std_level_${level.key}_boost_${sectionName}`}
                                className={styles.boostLabel}
                                onLoad={() => handleImageLoad(`${level.key}_boost`)}
                            />
                        }

                        {/* Background Asset Image */}
                        <Image
                            src={level.icon || ''}
                            alt={level.label}
                            className={styles.levelBackgroundImage}
                            id={`std_level_${level.key}_placeholder_${sectionName}`}
                            onLoad={() => handleImageLoad(level.key)}
                            manualAffix={(sectionName !== SECTION_NAMES.BELL_LINK_BOOST && sectionName !== SECTION_NAMES.VIP_BELL_LINK_BOOST) ? '.webp' : '.svg'}
                        />

                        {/* Currency Symbol */}
                        <span
                            ref={el => {
                                currencyRefs.current[level.key] = el;
                                return undefined;
                            }}
                            data-currency-level={level.key}
                            className={styles.currency + (level.boostValue ? ' ' + styles[`level_boost_${level.key}` as keyof typeof styles] : '')}
                        >
                            <StringMapper className={styles.currencyText} text={currency} />
                        </span>

                        {/* Level Value with Simple Animation */}
                        <div
                            ref={el => {
                                containerRefs.current[level.key] = el;
                                return undefined;
                            }}
                            className={[styles.levelValue, (level.boostValue ? styles[`level_boost_${level.key}` as keyof typeof styles] : '')].join(' ')}
                        >
                            {level.boostValue && <span className={styles.boostValue} data-boost-value>+{level.boostValue}</span>}
                            <span
                                ref={el => {
                                    numberRefs.current[level.key] = el;
                                    return undefined;
                                }}
                                className={styles.numberValue}
                                data-number-value
                            />
                        </div>

                    </div>
                    );
                })}
            </div>
        </div>
        {linkedJackpots?.length && <LinkedJackpots linkedJackpots={linkedJackpots} order={getLinkedJackpotsOrder()} sectionName={sectionName} />}
    </>);
};

export default memo(StandardJackpot);
